import React, { useEffect, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import { FinishedGoodsStockOverview } from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";

/**
 * Finished goods stock overview ("Gotovi proizvodi").
 * Shows which products have been produced (packaged) and how many pieces are
 * available in the warehouse, with a per-batch (lot) breakdown.
 */
const FinishedGoods = () => {
  savePathToLocalStorage();

  const [rows, setRows] = useState<FinishedGoodsStockOverview[]>([]);
  const [filterText, setFilterText] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  useEffect(() => {
    loadStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStock = async () => {
    try {
      setLoading(true);
      const data = await mesApi.getFinishedGoodsStock({
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      setRows(data || []);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d?: string | null) =>
    d ? new Date(d).toLocaleDateString("hr-HR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";

  const STATUS_LABELS: Record<string, string> = {
    planned: "Planirano",
    ready: "Spremno",
    in_progress: "U tijeku",
    completed: "Završeno",
    on_hold: "Pauza",
    skipped: "Preskočeno",
    canceled: "Otkazano",
  };

  const filtered = rows.filter((r) => {
    if (!filterText.trim()) return true;
    const q = filterText.toLowerCase();
    return (
      (r.product_name || "").toLowerCase().includes(q) ||
      (r.product_code || "").toLowerCase().includes(q)
    );
  });

  const totalPieces = filtered.reduce((acc, r) => acc + (r.total_pieces || 0), 0);
  const warehouseName = rows.find((r) => r.warehouse_name)?.warehouse_name || null;

  const estMassKg = (r: FinishedGoodsStockOverview): number | null => {
    // Prefer summing real batch masses; fall back to pieces × unit weight.
    const massFromBatches = r.batches.reduce(
      (acc, b) => acc + (b.final_mass_kg != null ? b.final_mass_kg : 0),
      0
    );
    if (massFromBatches > 0) return massFromBatches;
    if (r.unit_weight_g != null && r.total_pieces > 0) {
      return (r.unit_weight_g * r.total_pieces) / 1000;
    }
    return null;
  };

  return (
    <div className="mes-page">
      <Header title="MES - Skladište" />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          <ToastContainer position="top-center" autoClose={2500} />

          <div className="iwt-header">
            <div>
              <h2>Gotovi proizvodi</h2>
              <p className="iwt-role-hint">
                Što je proizvedeno (zapakirano) i koliko je na stanju
                {warehouseName ? ` u skladištu: ${warehouseName}` : ""}
              </p>
            </div>
          </div>

          <div className="wh-pregled-toolbar">
            <input
              type="text"
              placeholder="Pretraži proizvod..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="filter-select"
              style={{ minWidth: 220 }}
            />
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              Od:
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="filter-select"
              />
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              Do:
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="filter-select"
              />
            </label>
            <button type="button" className="btn-secondary" onClick={loadStock} disabled={loading}>
              Osvježi
            </button>
          </div>

          {loading ? (
            <p className="iwt-empty">Učitavanje...</p>
          ) : filtered.length === 0 ? (
            <p className="iwt-empty">Nema proizvedenih gotovih proizvoda na stanju</p>
          ) : (
            <>
              <div className="mes-section" style={{ marginBottom: 8 }}>
                <strong>{filtered.length}</strong> proizvoda ·{" "}
                <strong>{totalPieces.toLocaleString("hr-HR")}</strong> kom ukupno na stanju
                {warehouseName ? (
                  <>
                    {" "}
                    · skladište: <strong>{warehouseName}</strong>
                  </>
                ) : null}
              </div>
              <div className="wh-pregled-list">
                {filtered.map((r) => {
                  const open = expanded === r.product_id;
                  const mass = estMassKg(r);
                  return (
                    <div key={r.product_id} className="mes-section wh-pregled-card">
                      <button
                        type="button"
                        className="wh-pregled-card-head"
                        onClick={() => setExpanded(open ? null : r.product_id)}
                      >
                        <div>
                          <strong>{r.product_name || `Proizvod #${r.product_id}`}</strong>
                          <span className="wh-pregled-meta">
                            {r.product_code ? `${r.product_code} · ` : ""}
                            {r.batches.length} {r.batches.length === 1 ? "zamjes" : "zamjesa"}
                          </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                          <span style={{ fontFamily: "monospace", fontWeight: 600 }}>
                            {r.total_pieces.toLocaleString("hr-HR")} kom
                            {mass != null ? ` · ${mass.toFixed(1)} kg` : ""}
                          </span>
                          <span>{open ? "▾" : "▸"}</span>
                        </div>
                      </button>
                      {open && (
                        <div className="mes-table-wrap">
                          <table className="mes-table iwt-lines-table">
                          <thead>
                            <tr>
                              <th>Datum</th>
                              <th>Zamjes</th>
                              <th>Proizvodni LOT</th>
                              <th style={{ textAlign: "right" }}>Komada</th>
                              <th style={{ textAlign: "right" }}>Masa (kg)</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {r.batches.map((b) => (
                              <tr key={b.stock_id}>
                                <td>{formatDate(b.production_date || b.stock_date)}</td>
                                <td>{b.mix_code || "—"}</td>
                                <td style={{ fontFamily: "monospace" }}>
                                  {b.production_lot_code || "—"}
                                </td>
                                <td style={{ textAlign: "right", fontFamily: "monospace" }}>
                                  {b.quantity_pieces.toLocaleString("hr-HR")}
                                </td>
                                <td style={{ textAlign: "right", fontFamily: "monospace" }}>
                                  {b.final_mass_kg != null ? b.final_mass_kg.toFixed(2) : "—"}
                                </td>
                                <td>{b.batch_status ? STATUS_LABELS[b.batch_status] || b.batch_status : "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinishedGoods;
